import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileVideo, FileText, CheckCircle, Loader2 } from "lucide-react";

const API = "/api";

export default function ApplyPage() {
  const [formData, setFormData] = useState({ name: "", email: "", experience: "" });
  const [videoFile, setVideoFile] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const videoRef = useRef(null);
  const resumeRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !videoFile) {
      toast.error("Please fill in your name, email, and upload a video.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("email", formData.email);
      fd.append("experience", formData.experience);
      fd.append("video", videoFile);
      if (resumeFile) fd.append("resume", resumeFile);

      await axios.post(`${API}/recruitment/apply`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (error) {
      const msg = error?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video must be under 100MB");
      return;
    }
    setVideoFile(file);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardContent className="pt-10 pb-10 space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold text-slate-900">Application Submitted!</h2>
            <p className="text-slate-600">Thank you for applying to LanguaConnect English Class. We will review your application and contact you soon.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 py-8 px-4" data-testid="apply-page">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Join Our Teaching Team</h1>
          <p className="text-slate-600">LanguaConnect English Class is looking for passionate ESL tutors</p>
        </div>

        {/* Instructions */}
        <Card className="border-indigo-200 bg-indigo-50/50">
          <CardContent className="pt-5 pb-5">
            <h3 className="font-semibold text-indigo-900 mb-2">Video Instructions</h3>
            <ul className="text-sm text-indigo-800 space-y-1 list-disc list-inside">
              <li>Record a <strong>2-minute (max)</strong> video in English</li>
              <li>Introduce yourself and explain a topic in English</li>
              <li>Speak clearly and naturally as you would in a real class</li>
              <li>Ensure good audio quality (quiet environment, clear voice)</li>
            </ul>
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>Application Form</CardTitle>
            <CardDescription>All fields marked * are required</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" required data-testid="apply-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="your@email.com" required data-testid="apply-email" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience">Teaching Experience</Label>
                <Textarea id="experience" value={formData.experience} onChange={(e) => setFormData(p => ({ ...p, experience: e.target.value }))} placeholder="Describe your teaching experience, certifications, and what makes you a great tutor..." rows={4} data-testid="apply-experience" />
              </div>

              {/* Video Upload */}
              <div className="space-y-2">
                <Label>Video Introduction * (max 2 minutes)</Label>
                <div
                  onClick={() => videoRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
                  data-testid="video-upload-zone"
                >
                  <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/avi" onChange={handleVideoChange} className="hidden" />
                  {videoFile ? (
                    <div className="flex items-center justify-center gap-3 text-green-700">
                      <FileVideo className="w-8 h-8" />
                      <div>
                        <p className="font-medium">{videoFile.name}</p>
                        <p className="text-sm text-slate-500">{(videoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="w-8 h-8 mx-auto mb-2" />
                      <p className="font-medium">Click to upload video</p>
                      <p className="text-sm">MP4, WebM, MOV or AVI (max 100MB)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Resume Upload */}
              <div className="space-y-2">
                <Label>Resume (optional)</Label>
                <div
                  onClick={() => resumeRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                  data-testid="resume-upload-zone"
                >
                  <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} className="hidden" />
                  {resumeFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-700">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">{resumeFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-slate-500 text-sm">
                      <FileText className="w-5 h-5 mx-auto mb-1" />
                      <p>Click to upload resume (PDF, DOC)</p>
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-base font-semibold" data-testid="submit-application-btn">
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Uploading & Submitting...</>
                ) : (
                  "Submit Application"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
